import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { serverEvents, debounce, LIST_CHANGE_CODES, type ServerEvent } from '../lib/events.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface MallOrderItem {
  stockId: string;
  code: string;
  name: string;
  quantity: number;
  price: number;
}

interface MallOrder {
  id: string;
  refNo: string;
  storeCode: string;
  total: number;
  paymentMode: string;
  status: string;
  billCode: string;
  paymentProvider?: string;
  paymentId?: string;
  paymentStatus?: string;
  paymentCustomerAmount?: number;
  paymentFee?: number;
  paymentSettledAt?: number;
  shopperType?: string;
  kind?: string;
  saleId?: string;
  recalledAt?: number;
  createdAt: number;
  name: string;
  phone: string;
  email: string;
  items: MallOrderItem[];
}

const STATUS_FILTERS: Array<{ id: string; label: string }> = [
  { id: '', label: 'All' },
  { id: 'placed', label: 'New' },
  { id: 'available', label: 'Awaiting shopper' },
  { id: 'pending_confirmation', label: 'Saved (outlet offline)' },
  { id: 'pending', label: 'Pending payment' },
  { id: 'awaiting_payment', label: 'Awaiting payment' },
  { id: 'paid', label: 'Paid' },
  { id: 'ready', label: 'Ready' },
  { id: 'recalled', label: 'Recalled' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

/** Sale events that can move a mall order at the counter. */
const TRACKED_EVENTS = new Set(['salesHold', 'salesWholesalesHold', 'sales', 'salesWholesales']);

/**
 * Staff back-office: mall orders queue (mall.pharmapos.com).
 * Every order is saved at the outlet as a POS hold — members in Hold Sales,
 * wholesale customers in Hold Wholesales — so the cashier recalls it in the
 * desktop app to take payment and print the receipt. The queue follows the
 * hold's life at the counter (held → recalled → completed) and refreshes on
 * the outlet's sales/salesHold events, like the desktop receipt windows.
 */
@customElement('mall-orders')
export class MallOrders extends LitElement {
  @property() storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _orders: MallOrder[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _filter = '';
  @state() private _actionBusy = '';
  @state() private _expanded = '';
  @state() private _completeOrder: MallOrder | null = null;
  @state() private _completeMode = 'cash';
  @state() private _completeEInvoice = false;
  @state() private _completeError = '';
  @state() private _paymentsOpen = false;
  @state() private _paymentsLoading = false;
  @state() private _paymentsError = '';
  @state() private _payments: Array<Record<string, any>> = [];

  /** Reload guard — property bindings don't fire attributeChangedCallback. */
  private _activeKey = '';
  private _timer?: number;
  private _eventUnsubs: Array<() => void> = [];
  private _reloadSoon = debounce(() => { void this._load(true); }, 800);

  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; padding: 1rem; box-sizing: border-box; }
    .tools { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; flex-shrink: 0; margin-bottom: 0.75rem; }
    .tools h2 { margin: 0 0.5rem 0 0; font-size: 1.05rem; }
    .error { margin-bottom: 0.5rem; }
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .table-wrap { flex: 1; min-height: 0; overflow: auto; }
    thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }
    tbody td { vertical-align: top; }
    .status { display: inline-block; padding: 0.15rem 0.6rem; border-radius: 999px; font-size: 0.76rem; font-weight: 600; background: #eef; }
    .status.placed { background: #e3f2fd; }
    .status.pending_confirmation { background: #fff8e1; color: #8a6d00; }
    .status.available { background: #e8f5e9; color: #1b5e20; }
    .status.unavailable { background: #ffebee; color: #c62828; }
    .status.queued { background: #eef; }
    .status.pending { background: #fff8e1; }
    .status.awaiting_payment { background: #fff8e1; }
    .status.paid { background: #e8f5e9; }
    .status.ready { background: #ede7f6; }
    .status.recalled { background: #fff3e0; color: #e65100; }
    .status.completed { background: #eceff1; color: #607d8b; }
    .status.cancelled { background: #ffebee; color: #c62828; }
    .actions { display: flex; gap: 0.35rem; flex-wrap: wrap; }
    .detail td { background: rgba(0,0,0,0.02); }
    .detail ul { margin: 0.25rem 0; padding-left: 1.2rem; }
    /* bordered variant of the shared buttons */
    .btn:not(.primary):not(.danger) { background: var(--color-surface); color: var(--color-text); border-color: var(--color-border); }
    .btn:not(.primary):not(.danger):hover:not(:disabled) { background: #f4f7fb; filter: none; }
    .btn.danger { background: var(--color-surface); color: var(--color-error); border-color: var(--color-error); }
  `];

  protected updated() {
    // Property bindings don't fire attributeChangedCallback and Lit reuses
    // the element when switching outlets — reload only on a real store change.
    if (!this.storeCode) return;
    if (this._activeKey !== this.storeCode) {
      this._activeKey = this.storeCode;
      this._orders = [];
      this._expanded = '';
      this._load();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._timer = window.setInterval(() => this._load(true), 30000);
    // Live refresh — the outlet emits the same events the desktop receipt
    // windows listen to (ServerEventMapping sales/salesHold). A recalled hold
    // publishes salesHold; closing the sale publishes sales/salesWholesales.
    // The reload asks the gateway to re-check each active hold, so the list
    // shows Recalled → Completed without waiting for the 30 s poll.
    this._eventUnsubs = [
      ...Array.from(TRACKED_EVENTS).map((type) => serverEvents.on(type, this._onServerEvent)),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timer) window.clearInterval(this._timer);
    this._reloadSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
  }

  /** Outlet change that can affect a hold (created / recalled / sale closed). */
  private _onServerEvent = (ev: ServerEvent) => {
    if (!this.storeCode) return;
    if (ev.store && ev.store !== this.storeCode) return;
    if (!LIST_CHANGE_CODES.has(ev.code)) return;
    this._reloadSoon();
  };

  /** Socket (re)connected or ppServer came back — events may have been missed. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync' || !this.storeCode) return;
    this._reloadSoon();
  };

  private async _load(quiet = false) {
    if (!this.storeCode) return;
    if (!quiet) this._loading = true;
    this._error = '';
    try {
      const res = await api.mallStaffOrders(this.storeCode, this._filter || undefined);
      this._orders = (res.data || []) as MallOrder[];
    } catch (err: any) {
      if (!quiet) this._error = err.message || 'Failed to load mall orders';
    } finally {
      this._loading = false;
    }
  }

  private _setFilter(id: string) {
    this._filter = id;
    this._load();
  }

  private async _openPayments() {
    this._paymentsOpen = true;
    this._paymentsLoading = true;
    this._paymentsError = '';
    try {
      const res = await api.payments(this.storeCode, { limit: 100 });
      this._payments = (res.data || []) as Array<Record<string, any>>;
    } catch (err: any) {
      this._paymentsError = err.message || 'Failed to load payments';
    } finally {
      this._paymentsLoading = false;
    }
  }

  private _payBadge(status?: string): string {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  private _toggleExpand(id: string) {
    this._expanded = this._expanded === id ? '' : id;
  }

  private _statusLabel(status: string): string {
    switch (status) {
      case 'placed': return 'New';
      // Offline-order lifecycle: saved while the outlet was down, then
      // re-validated; the shopper completes checkout from the storefront.
      case 'pending_confirmation': return 'Saved (outlet offline)';
      case 'available': return 'Awaiting shopper';
      case 'unavailable': return 'Items unavailable';
      case 'queued': return 'Sending to outlet';
      case 'rejected': return 'Rejected';
      case 'expired': return 'Expired';
      case 'pending': return 'Pending payment';
      case 'awaiting_payment': return 'Awaiting payment';
      case 'paid': return 'Paid';
      case 'ready': return 'Ready';
      case 'recalled': return 'Recalled';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  private _holdLabel(order: MallOrder): string {
    if (order.kind !== 'hold') return 'Sales order (legacy)';
    return order.shopperType === 'customer' ? 'Hold Wholesales' : 'Hold Sales';
  }

  /** Where the counter hold is right now. */
  private _holdState(order: MallOrder): string {
    if (order.kind !== 'hold') return '';
    switch (order.status) {
      case 'pending':
        return order.paymentMode === 'online'
          ? 'Waiting for the online payment — the sale is saved automatically on success'
          : 'Waiting at the counter';
      case 'paid': return 'Payment confirmed — saving the sale at the outlet';
      case 'recalled': return 'Recalled into the POS — awaiting payment';
      case 'completed':
        return order.saleId
          ? `${order.paymentMode === 'online' ? 'Paid online · sale saved' : 'Closed at the POS'} · sale #${order.saleId}`
          : 'Completed';
      case 'cancelled': return 'Released from the counter';
      default: return 'Waiting at the counter';
    }
  }

  /** A recalled hold is owned by the POS; only "close" makes sense there. */
  private _canCancel(order: MallOrder): boolean {
    return order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'recalled';
  }

  private async _markReady(order: MallOrder) {
    this._actionBusy = order.id;
    this._error = '';
    try {
      await api.mallStaffOrderReady(this.storeCode, order.id);
      await this._load(true);
    } catch (err: any) {
      this._error = err.message || 'Failed';
    } finally {
      this._actionBusy = '';
    }
  }

  private _openComplete(order: MallOrder) {
    this._completeOrder = order;
    this._completeMode = order.paymentMode === 'online' ? 'online' : 'cash';
    this._completeEInvoice = false;
    this._completeError = '';
  }

  private async _submitComplete() {
    const order = this._completeOrder;
    if (!order) return;
    this._actionBusy = order.id;
    this._completeError = '';
    try {
      await api.mallStaffOrderComplete(this.storeCode, order.id, {
        paymentMode: this._completeMode,
        submitEInvoice: this._completeEInvoice,
      });
      this._completeOrder = null;
      await this._load(true);
    } catch (err: any) {
      this._completeError = err.message || 'Failed to complete the order';
    } finally {
      this._actionBusy = '';
    }
  }

  private async _cancel(order: MallOrder) {
    const extra = order.paymentMode === 'online' && order.status === 'paid'
      ? '\nThe online payment was already received — the refund is handled manually in toyyibPay.'
      : '';
    if (!window.confirm(`Cancel order ${order.refNo}?${extra}`)) return;
    this._actionBusy = order.id;
    this._error = '';
    try {
      await api.mallStaffOrderCancel(this.storeCode, order.id);
      await this._load(true);
    } catch (err: any) {
      this._error = err.message || 'Failed';
    } finally {
      this._actionBusy = '';
    }
  }

  render() {
    return html`
      <div class="tools">
        <h2>🛍️ Mall Orders</h2>
        ${STATUS_FILTERS.map((f) => html`
          <button class="chip ${this._filter === f.id ? 'selected' : ''}"
            @click=${() => this._setFilter(f.id)}>${f.label}</button>
        `)}
        <span class="spacer"></span>
        <button class="btn" @click=${() => void this._openPayments()}>💳 Payments</button>
        <button class="btn" ?disabled=${this._loading} @click=${() => this._load()}>
          ${this._loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}

      <div class="tab-body">
        <div class="table-wrap">
          ${this._orders.length === 0 && !this._loading
            ? html`<div class="empty">No mall orders ${this._filter ? `with status "${this._statusLabel(this._filter)}"` : 'yet'}.</div>`
            : html`
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Ref</th>
                  <th>Customer</th>
                  <th class="num">Items</th>
                  <th class="num">Total</th>
                  <th>POS hold</th>
                  <th>Payment</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                ${this._orders.map((o) => html`
                  <tr>
                    <td><span class="status ${o.status}">${this._statusLabel(o.status)}</span></td>
                    <td style="cursor:pointer" title="Show items" @click=${() => this._toggleExpand(o.id)}>${o.refNo || o.id.slice(0, 8)}</td>
                    <td>${o.name || o.email}<br><span class="muted">${o.phone || ''}</span></td>
                    <td class="num">${(o.items || []).reduce((n, i) => n + (i.quantity || 0), 0)}</td>
                    <td class="num">${(o.total ?? 0).toFixed(2)}</td>
                    <td>
                      <div>${this._holdLabel(o)}</div>
                      <div class="muted">${this._holdState(o)}</div>
                    </td>
                    <td>
                      ${o.paymentMode === 'online'
                        ? html`<div>${o.paymentProvider === 'billplz' ? 'Billplz' : (o.paymentProvider === 'toyyibpay' ? 'ToyyibPay' : 'Online')}</div>
                            <span class="status ${o.paymentStatus === 'paid' || o.paymentStatus === 'settled' ? 'paid' : (o.paymentStatus === 'failed' || o.paymentStatus === 'expired' ? 'cancelled' : 'awaiting_payment')}">
                              ${this._payBadge(o.paymentStatus || (o.status === 'paid' ? 'paid' : 'pending'))}
                            </span>`
                        : 'At counter'}
                    </td>
                    <td>${o.createdAt ? new Date(o.createdAt * 1000).toLocaleString() : ''}</td>
                    <td>
                      <div class="actions">
                        ${o.status === 'placed' || o.status === 'paid' ? html`
                          <button class="btn" ?disabled=${this._actionBusy === o.id}
                            @click=${() => this._markReady(o)}>Ready</button>` : ''}
                        ${o.status !== 'completed' && o.status !== 'cancelled' ? html`
                          <button class="btn primary" ?disabled=${this._actionBusy === o.id}
                            @click=${() => this._openComplete(o)}>Close</button>` : ''}
                        ${this._canCancel(o) ? html`
                          <button class="btn danger" ?disabled=${this._actionBusy === o.id}
                            @click=${() => this._cancel(o)}>Cancel</button>` : ''}
                      </div>
                    </td>
                  </tr>
                  ${this._expanded === o.id ? html`
                    <tr class="detail">
                      <td colspan="9">
                        <ul>
                          ${(o.items || []).map((it) => html`
                            <li>${it.name || it.code} × ${it.quantity} — RM ${(it.price * it.quantity).toFixed(2)}</li>
                          `)}
                        </ul>
                        ${o.paymentMode === 'online' ? html`
                          <div class="muted">
                            ${o.paymentProvider === 'billplz' ? 'Billplz' : (o.paymentProvider === 'toyyibpay' ? 'ToyyibPay' : 'Online')}
                            · ${this._payBadge(o.paymentStatus || 'pending')}
                            ${o.billCode ? html` · Bill: ${o.billCode}` : ''}
                          </div>
                          ${o.paymentCustomerAmount
                            ? html`<div class="muted">Customer paid RM ${o.paymentCustomerAmount.toFixed(2)}
                                ${o.paymentFee ? html` (fee RM ${o.paymentFee.toFixed(2)})` : ''}</div>` : ''}
                          ${o.paymentSettledAt
                            ? html`<div class="muted">Settled: ${new Date(o.paymentSettledAt * 1000).toLocaleString()}</div>` : ''}
                        ` : ''}
                        ${o.recalledAt
                          ? html`<div class="muted">Recalled at the POS: ${new Date(o.recalledAt * 1000).toLocaleString()}</div>` : ''}
                        ${o.saleId ? html`<div class="muted">Closed sale: #${o.saleId}</div>` : ''}
                        <div class="muted">${o.email}</div>
                      </td>
                    </tr>` : ''}
                `)}
              </tbody>
            </table>`}
        </div>
      </div>

      ${this._completeOrder ? html`
        <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._completeOrder = null; }}>
          <div class="modal">
            <h3>${this._completeOrder.kind === 'hold' ? 'Close' : 'Complete'} ${this._completeOrder.refNo || this._completeOrder.id.slice(0, 8)}</h3>
            ${this._completeOrder.kind === 'hold' ? html`
              <div class="muted" style="margin-bottom:0.75rem">
                ${this._completeOrder.paymentMode === 'online' && this._completeOrder.status !== 'recalled'
                  ? html`Total RM ${this._completeOrder.total.toFixed(2)} — this online order is
                      <b>${this._completeOrder.paymentStatus === 'paid' || this._completeOrder.paymentStatus === 'settled'
                        ? 'paid; the outlet is saving the sale'
                        : 'waiting for the gateway payment'}</b>
                      as ${this._holdLabel(this._completeOrder)}. When the payment succeeds the outlet
                      saves the sale automatically and the order moves to Completed; closing here does
                      not charge the shopper.`
                  : this._completeOrder.status === 'recalled'
                    ? html`Total RM ${this._completeOrder.total.toFixed(2)} — this hold was already
                        <b>recalled into the POS</b> and the cashier is closing the sale there.
                        Closing the mall order here only records the outcome (nothing is charged or deleted).`
                    : html`Total RM ${this._completeOrder.total.toFixed(2)} — this order is already waiting in the desktop POS as
                        <b>${this._holdLabel(this._completeOrder)}</b>. Recall it there to take payment and print the receipt.
                        Closing here removes the hold from the counter (it is not charged again here).`}
              </div>
            ` : html`
              <div class="muted" style="margin-bottom:0.75rem">
                Total RM ${this._completeOrder.total.toFixed(2)} — the sale is recorded at the outlet (stock deducted, loyalty points awarded).
              </div>
            `}
            ${this._completeError ? html`<div class="error">${this._completeError}</div>` : ''}
            ${this._completeOrder.kind === 'hold' ? nothing : html`
              <div class="form-group">
                <label>Payment received</label>
                <select .value=${this._completeMode} @change=${(e: any) => { this._completeMode = e.target.value; }}>
                  ${this._completeOrder.paymentMode === 'online' ? html`
                    <option value="online" ?selected=${this._completeOrder.status === 'paid'}>Online (paid) ✓</option>` : ''}
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <label style="display:flex;gap:0.4rem;align-items:center;font-size:0.85rem">
                <input type="checkbox" .checked=${this._completeEInvoice}
                  @change=${(e: any) => { this._completeEInvoice = e.target.checked; }}>
                Submit e-invoice
              </label>
            `}
            <div class="modal-footer">
              <button class="btn" @click=${() => { this._completeOrder = null; }}>Cancel</button>
              <button class="btn primary" ?disabled=${this._actionBusy === this._completeOrder.id}
                @click=${() => this._submitComplete()}>${this._completeOrder.kind === 'hold' ? 'Close order' : 'Complete'}</button>
            </div>
          </div>
        </div>` : ''}

      ${this._paymentsOpen ? html`
        <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._paymentsOpen = false; }}>
          <div class="modal" style="max-width:900px" @click=${(e: Event) => e.stopPropagation()}>
            <h3>💳 Online payments</h3>
            <div class="muted" style="margin-bottom:0.75rem">
              Every online payment submitted at this outlet. “Settled” is reported by ToyyibPay;
              Billplz does not report per-bill settlement through its API.
            </div>
            ${this._paymentsError ? html`<div class="error">${this._paymentsError}</div>` : ''}
            ${this._paymentsLoading ? html`<div class="state-message">Loading payments…</div>` : html`
              <div style="max-height:55vh;overflow:auto">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Ref</th>
                      <th>Provider</th>
                      <th class="num">Order</th>
                      <th class="num">Fee</th>
                      <th class="num">Customer</th>
                      <th>Settlement</th>
                      <th>Submitted</th>
                      <th>Paid / Settled</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this._payments.map((p) => html`
                      <tr>
                        <td><span class="status ${p.status === 'paid' || p.status === 'settled' ? 'paid' : (p.status === 'failed' || p.status === 'expired' ? 'cancelled' : 'awaiting_payment')}">
                          ${this._payBadge(String(p.status || ''))}</span></td>
                        <td>${p.orderRef || p.orderId || ''}</td>
                        <td>${p.provider === 'billplz' ? 'Billplz' : 'ToyyibPay'}</td>
                        <td class="num">${Number(p.orderAmount || 0).toFixed(2)}</td>
                        <td class="num">${Number(p.feeAmount || 0).toFixed(2)}</td>
                        <td class="num">${Number(p.customerAmount || 0).toFixed(2)}</td>
                        <td>${p.settlementStatus === 'settled'
                          ? html`${p.settlementRef || 'Settled'}<br><span class="muted">${p.settledAt ? new Date(Number(p.settledAt) * 1000).toLocaleString() : ''}</span>`
                          : (p.settlementStatus || '—')}</td>
                        <td>${p.submittedAt ? new Date(Number(p.submittedAt) * 1000).toLocaleString() : ''}</td>
                        <td>${p.paidAt ? new Date(Number(p.paidAt) * 1000).toLocaleString() : ''}</td>
                      </tr>
                    `)}
                  </tbody>
                </table>
                ${this._payments.length === 0 ? html`<div class="empty">No online payments yet.</div>` : ''}
              </div>
            `}
            <div class="modal-footer">
              <button class="btn" @click=${() => { this._paymentsOpen = false; }}>Close</button>
              <button class="btn" ?disabled=${this._paymentsLoading} @click=${() => void this._openPayments()}>↻ Refresh</button>
            </div>
          </div>
        </div>` : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mall-orders': MallOrders;
  }
}
